/**
 * iter-230j — Artifact-driven onboarding hook.
 *
 * Fetches the live verdict from `/api/vendors/onboarding-artifacts/me`
 * once per session and caches it in sessionStorage so PrivateRoute /
 * route changes do not re-fire the network call on every navigation.
 *
 * Invalidation:
 *   - Manual: call `refresh()` after a step is completed (e.g. on the
 *     final "Complete Registration" handler in VendorTaxRegistration).
 *   - Automatic: on user.id change (logout / re-login).
 *
 * Failure policy: if the API errors / times out, return `null` artifacts
 * and `loading=false`. The PrivateRoute treats `null` as "unknown — fall
 * back to the legacy flag-driven check" so we never lock a vendor out
 * of their own portal because of a transient network blip.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';

const CACHE_KEY_PREFIX = 'onboarding_artifacts_v2_';
const OLD_CACHE_KEY_PREFIXES = ['onboarding_artifacts_v1_'];
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 min

// iter-230bd — In-memory invalidation event. `clearOnboardingArtifactsCache`
// only wiped the sessionStorage row, but the React state `artifacts` held
// by every active `useOnboardingArtifacts` instance stayed stale until a
// `user.id` change re-armed the effect. That left a window where:
//   1. Vendor finishes Start Trial → updateUser({onboarding_completed:true})
//      → clearOnboardingArtifactsCache(id) → navigate('/dashboard')
//   2. PrivateRoute re-renders with user.onboarding_completed=true BUT the
//      cached React `artifacts` STILL says `{completed:false}`.
//   3. For pending-status vendors PrivateRoute then redirects to
//      `artifacts.next_step_route` → loop back to /vendor/tax-registration.
// Emitting a CustomEvent here forces every mounted hook instance to
// drop its in-memory verdict and trigger an immediate re-fetch.
const INVALIDATION_EVENT = 'onboarding-artifacts-invalidated';

// iter-230aq — Sweep cache entries written under any older version
// prefix. This runs at module-load time, ONCE per tab. Without it,
// vendors stuck in the iter-230ap subscription loop kept reading
// their pre-deploy `completed: false` verdict from sessionStorage —
// even after logout+login in the same browser tab — because the
// previous logout cleared `user`/`token` but NOT the artifacts cache.
(() => {
  try {
    if (typeof window === 'undefined') return;
    Object.keys(sessionStorage)
      .filter((k) => OLD_CACHE_KEY_PREFIXES.some((p) => k.startsWith(p)))
      .forEach((k) => sessionStorage.removeItem(k));
  } catch (_) { /* sessionStorage disabled — nothing to clean */ }
})();

const readCache = (vendorId) => {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY_PREFIX + vendorId);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.fetchedAt || Date.now() - parsed.fetchedAt > CACHE_TTL_MS) return null;
    return parsed.verdict;
  } catch (_) {
    return null;
  }
};

const writeCache = (vendorId, verdict) => {
  try {
    sessionStorage.setItem(
      CACHE_KEY_PREFIX + vendorId,
      JSON.stringify({ verdict, fetchedAt: Date.now() })
    );
  } catch (_) { /* ignore quota */ }
};

export const clearOnboardingArtifactsCache = (vendorId) => {
  try {
    if (vendorId) sessionStorage.removeItem(CACHE_KEY_PREFIX + vendorId);
    else {
      Object.keys(sessionStorage)
        .filter((k) => k.startsWith(CACHE_KEY_PREFIX))
        .forEach((k) => sessionStorage.removeItem(k));
    }
  } catch (_) { /* ignore */ }
  // iter-230bd — Fire the in-memory invalidation event so every mounted
  // `useOnboardingArtifacts` instance immediately drops its stale React
  // state and re-fetches. Without this, sessionStorage-only invalidation
  // is half-done and PrivateRoute keeps seeing the pre-completion verdict.
  try {
    if (typeof window !== 'undefined' && typeof CustomEvent === 'function') {
      window.dispatchEvent(new CustomEvent(INVALIDATION_EVENT, {
        detail: { vendorId: vendorId || null },
      }));
    }
  } catch (_) { /* ignore */ }
};

export const useOnboardingArtifacts = () => {
  const { user, apiCall } = useAuth();
  const [artifacts, setArtifacts] = useState(() =>
    user?.id ? readCache(user.id) : null
  );
  const [loading, setLoading] = useState(() => {
    // Skip loading state entirely for non-vendors or when we have a cache.
    if (!user?.id) return false;
    if (user.role !== 'vendor') return false;
    return !readCache(user.id);
  });
  const inflightRef = useRef(null);
  // iter-230p — AbortController so we kill an in-flight verdict fetch
  // when the user identity changes (logout, switch-account, fresh login
  // after password reset). Without this, a stale fetch for user A could
  // race the new fetch for user B and stamp B's state with A's verdict.
  const abortRef = useRef(null);

  const fetchArtifacts = useCallback(async () => {
    if (!user?.id || user.role !== 'vendor') {
      setArtifacts({ completed: true, missing: [], role: user?.role });
      setLoading(false);
      return;
    }
    if (inflightRef.current) return inflightRef.current;
    // Abort any prior in-flight call from a previous user identity.
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch (_) { /* ignore */ }
    }
    const controller = new AbortController();
    abortRef.current = controller;
    const fetchingForUserId = user.id;
    setLoading(true);
    const promise = (async () => {
      try {
        const data = await apiCall('/api/vendors/onboarding-artifacts/me', {
          signal: controller.signal,
          silentAuth: true, // background call — do NOT trigger logout on 401 token expiry
        });
        // Guard: by the time the response arrives, the user identity
        // may have changed. Drop the result if so — the new identity
        // will have its own fetch in flight.
        if (controller.signal.aborted || fetchingForUserId !== user?.id) {
          return;
        }
        if (data) {
          writeCache(user.id, data);
          setArtifacts(data);
        }
      } catch (e) {
        // Fail-open: keep artifacts=null so PrivateRoute can fall back
        // to the legacy flag-driven check rather than locking out a
        // vendor because the verdict API blipped.
        if (e?.name !== 'AbortError') {
          // eslint-disable-next-line no-console
          console.warn('[useOnboardingArtifacts] failed:', e?.message);
        }
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null;
        }
        // Only flip loading off if we're still on the same identity.
        if (fetchingForUserId === user?.id) {
          setLoading(false);
        }
        inflightRef.current = null;
      }
    })();
    inflightRef.current = promise;
    return promise;
  }, [user?.id, user?.role, apiCall]);

  useEffect(() => {
    // Re-fetch only when the vendor identity changes (login / logout).
    if (!user?.id) {
      // iter-230p — clear any leftover artifact state on logout so the
      // next user's PrivateRoute doesn't briefly see the prior user's
      // verdict before the new fetch returns.
      if (abortRef.current) {
        try { abortRef.current.abort(); } catch (_) { /* ignore */ }
        abortRef.current = null;
      }
      setArtifacts(null);
      setLoading(false);
      return;
    }
    if (user.role !== 'vendor') {
      setArtifacts({ completed: true, missing: [], role: user.role });
      setLoading(false);
      return;
    }
    const cached = readCache(user.id);
    if (cached) {
      setArtifacts(cached);
      setLoading(false);
      return;
    }
    // iter-230p — Important: explicitly clear stale state from the
    // previous identity BEFORE kicking off the new fetch. Otherwise
    // PrivateRoute could briefly see user A's `{completed:true}`
    // verdict applied to user B during the loading window.
    setArtifacts(null);
    fetchArtifacts();
  }, [user?.id, user?.role, fetchArtifacts]);

  // iter-230bd — Listen for explicit cache invalidations (fired by
  // `clearOnboardingArtifactsCache` at the tail of completion handlers
  // like Start Trial). Drop the React state and re-fetch immediately
  // so PrivateRoute sees the post-completion verdict on its very next
  // evaluation. Without this, the dashboard redirect would race the
  // user's button click and loop back to /vendor/tax-registration.
  useEffect(() => {
    if (!user?.id || user.role !== 'vendor') return undefined;
    const handler = (e) => {
      const targetId = e?.detail?.vendorId;
      // null target means "all vendors" — applies to us. Otherwise must match.
      if (targetId && targetId !== user.id) return;
      setArtifacts(null);
      setLoading(true);
      fetchArtifacts();
    };
    window.addEventListener(INVALIDATION_EVENT, handler);
    return () => window.removeEventListener(INVALIDATION_EVENT, handler);
  }, [user?.id, user?.role, fetchArtifacts]);

  return { artifacts, loading, refresh: fetchArtifacts };
};

export default useOnboardingArtifacts;
